<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import FullPageIcon from '@/components/icons/FullPageIcon.vue';
import { LazyTooltip } from '@/components/ui/tooltip';
import { areaEntity, entityGate, recordPathFor } from '@/navigation/areas';
import { isGateOpen } from '@/navigation/gates';
import { useInspectorStore, type InspectorTrailStep } from '@/stores/inspectorStore';

/**
 * The header of every peek: the side peek and the phone's bottom sheet. From
 * left to right:
 *
 * - Back and forward, which step through the stack of peeks one at a time.
 * - The trail, such as "Angelica Waelchi III › Main St Café › Patio". The last
 *   step is the record shown. Each earlier step is a button back to it. The
 *   first step, when it is the record the peeks were opened from, closes them.
 * - The record's own ⋯ menu, which holds Customize page…, and which it puts in
 *   `#inspector-header-actions`.
 * - The full-page button, which opens the shown record's page and closes the
 *   peeks. It shows only for a record with a page the person may open.
 * - Close, which closes the whole stack.
 *
 * There is one kind of peek, so there is no peek-mode button. Spec:
 * docs/specs/record-navigation-layers.md, "The side peek".
 */

const inspectorStore = useInspectorStore();
const router = useRouter();

const fullPagePath = computed(() => {
    const record = inspectorStore.currentRecord;
    if (!record || !areaEntity(record.entityId) || !isGateOpen(entityGate(record.entityId))) return null;
    return recordPathFor(record.entityId, record.recordId);
});

const earlierSteps = computed(() => inspectorStore.trail.slice(0, -1));
const currentStep = computed(() => inspectorStore.trail[inspectorStore.trail.length - 1] ?? null);

function goToStep(step: InspectorTrailStep) {
    if (step.kind === 'origin') {
        inspectorStore.close();
        return;
    }
    inspectorStore.popTo(step.index);
}

function openFullPage() {
    if (!fullPagePath.value) return;
    const path = fullPagePath.value;
    inspectorStore.close();
    void router.push(path);
}
</script>

<template>
  <div class="flex shrink-0 items-center gap-2 px-4 py-1.5 sm:px-6" data-testid="peek-header">
    <!-- Each button sits in a span. A disabled button gets no pointer events,
         so when there is nowhere to go the span is what shows the tooltip. -->
    <div class="-ml-1 flex shrink-0 items-center gap-0.5">
      <LazyTooltip side="bottom" align="start">
        <span class="inline-flex shrink-0" data-testid="inspector-back-tooltip-target">
          <button
            type="button"
            class="hover:bg-muted inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
            aria-label="Back"
            data-testid="inspector-back"
            :disabled="!inspectorStore.canGoBack"
            @click="inspectorStore.pop()"
          >
            <ChevronLeftIcon class="h-4 w-4" />
          </button>
        </span>
        <template #content>Back</template>
      </LazyTooltip>
      <LazyTooltip side="bottom">
        <span class="inline-flex shrink-0" data-testid="inspector-forward-tooltip-target">
          <button
            type="button"
            class="hover:bg-muted inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
            aria-label="Forward"
            data-testid="inspector-forward"
            :disabled="!inspectorStore.canGoForward"
            @click="inspectorStore.forward()"
          >
            <ChevronRightIcon class="h-4 w-4" />
          </button>
        </span>
        <template #content>Forward</template>
      </LazyTooltip>
    </div>

    <!-- The trail. Long names are cut short, and hovering one shows it whole. -->
    <nav aria-label="Peek trail" class="flex min-w-0 flex-1 items-center gap-1 text-xs text-muted-foreground" data-testid="peek-trail">
      <template v-for="step in earlierSteps" :key="`${step.kind}-${step.index}`">
        <button
          type="button"
          class="min-w-0 max-w-[10rem] shrink truncate rounded underline-offset-2 hover:text-foreground hover:underline"
          :title="step.kind === 'origin' ? `Back to ${step.title}, closing the peeks` : `Back to ${step.title}`"
          data-testid="peek-trail-step"
          :data-kind="step.kind"
          @click="goToStep(step)"
        >
          {{ step.title }}
        </button>
        <span aria-hidden="true" class="shrink-0">›</span>
      </template>
      <span
        v-if="currentStep"
        class="min-w-0 truncate font-medium text-foreground"
        :title="currentStep.title"
        aria-current="page"
        data-testid="peek-trail-current"
      >{{ currentStep.title }}</span>
    </nav>

    <div class="-mr-1 flex shrink-0 items-center gap-0.5">
      <!-- The record shown here puts its own ⋯ menu here. -->
      <div id="inspector-header-actions" class="flex items-center gap-0.5" />
      <LazyTooltip v-if="fullPagePath" side="bottom">
        <button
          type="button"
          class="hover:bg-muted inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Open the full page"
          data-testid="peek-open-full-page"
          @click="openFullPage"
        >
          <FullPageIcon class="h-4 w-4" />
        </button>
        <template #content>Open the full page</template>
      </LazyTooltip>
      <LazyTooltip side="bottom" align="end">
        <button
          type="button"
          class="hover:bg-muted inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Close inspector"
          data-testid="inspector-close"
          @click="inspectorStore.close()"
        >
          <XMarkIcon class="h-4 w-4" />
        </button>
        <template #content>Close (Esc)</template>
      </LazyTooltip>
    </div>
  </div>
</template>
